import { useState, useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ActionButton from '../components/StepperForm/ActionButton';
import StepperForm from '../components/StepperForm/StepperForm';
import MainStep from '../components/StepperForm/MainStep';
import FeesStep from '../components/StepperForm/FeesStep';
import PrincipalsStep from '../components/StepperForm/PrincipalsStep';
import ProfileStep from '../components/StepperForm/ProfileStep';
import EquipmentStep from '../components/StepperForm/EquipmentStep';
import DocsStep from '../components/StepperForm/DocsStep';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { getDBAById, submitAllStepperData, updateDBA } from '../store/slices/merchantsSlice';
import ErrorBoundary from '../components/ErrorBoundary';
import { getAttributeTypes } from '../store/slices/lookupSlice';
import PageLayout from '../components/layout/PageLayout';
import { getValidationErrorDetails } from '../utils/errorHandler';

const steps = [
  'MAIN',
  'PROFILE',
  'PRINCIPALS',
  'FEES',
  'EQUIPMENT',
  'DOCS',
];

const stepPayloadMap = {
  0: 'dba',
  1: 'merchant',
  2: 'principals',
  3: 'equipment', // Fees step maps to equipment for simplicity; adjust if needed
  4: 'equipment',
  5: 'attachments',
};


const CreateMerchant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [merchantData, setMerchantData] = useState(null);
  const [missingItems, setMissingItems] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [sourceType, setSourceType] = useState('normal'); // 'normal' or 'lead'
  const [selectedLeadId, setSelectedLeadId] = useState(''); // Store the lead ID from URL
  const [leadIdFromUrl, setLeadIdFromUrl] = useState(''); // ← Add this
  // Form data collection state
  const [formData, setFormData] = useState({
    dba_name: '',
    legal_name: '',
    contact_name: '',
    contact_phone: '',
    ssn: '',
    status: 1,
    type: '',
    dba_phone: '',
    dba_email: '',
    lead_id: '', // Add this
    type: 'normal', // Add this - 'normal' or 'lead'

    dba_address: {
      street: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'US'
    },
    legal_address: {
      street: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'US'
    },
    business_type: '',
    industry: '',
    years_in_business: 0,
    months_in_business: 0,
    annual_revenue: 0,
    avg_month_sales: 0,
    number_of_locations: 1,
    ownership_type: '',
    business_category: '',
    return_policy: '',
    delivery_days: '',
    website: '',
    tax_id: '',
    business_name: '',
    business_description: '',
    mid_number: '',
    banking: {
      bank_name: '',
      routing_number: '',
      bussiness_title: '',
      business_description: '',
      title: '',
      account_number: '',
      confirm_account_number: '',
      account_type: 'checking',
      account_owner: '',
      full_name: '',
      business_name: '',
      
      fee_deduct_other: false,
      present_transaction: 0,
      moto_transaction: 0,
      ecom_transaction: 0
    },
    principals: [],
    fees: {
      fee_category: 'Pass Through',
      use_fee_template: false
    },
    equipment: [],
    equipment_notes: [],
    documents: [],
    attachments: [],
    dynamic_attributes: {},
  });

  // Edit mode detection and initial data fetching
  // Edit mode detection and initial data fetching
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editParam = urlParams.get('edit');
    const idParam = urlParams.get('id');
    const leadIdParam = urlParams.get('lead_id');

    if (leadIdParam) {  
      setSourceType('sales');
      setSelectedLeadId(leadIdParam);
      setLeadIdFromUrl(leadIdParam); // ← Persist it here

      setFormData(prev => ({
        ...prev,
        lead_id: leadIdParam,
        type: 'sales'
      }));
    } else {
      setSourceType('normal');
    }

    if (editParam === 'true' && idParam) {
      setIsEditMode(true);
      setEditId(idParam);
      fetchMerchantData(idParam);
    }
  }, [location.search]);

  // Fetch merchant data for edit mode or after step change
  const fetchMerchantData = async (id) => {
    try {
      setIsLoadingData(true);
      const response = await dispatch(getDBAById(id)).unwrap();
      setMerchantData(response);
      const transformedData = transformApiDataToFormData(response);
      setFormData(transformedData);
      // toast.success('Merchant data loaded successfully!');
    } catch (error) {
      console.error('❌ Error fetching merchant data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch data when step changes (except first step in create mode)
  useEffect(() => {
    if (isEditMode && editId && activeStep > 0) {
      fetchMerchantData(editId);
    }
  }, [activeStep, isEditMode, editId]);

  // Transform API data to form data format
  const transformApiDataToFormData = (apiResponse) => {
    const apiData = apiResponse.data || apiResponse;
    const transformedAttributes = {};
    if (apiData.attribute_types && Array.isArray(apiData.attribute_types)) {
      apiData.attribute_types.forEach(attributeType => {
        if (attributeType.attributes && Array.isArray(attributeType.attributes)) {
          attributeType.attributes.forEach(attribute => {
            if (attribute.attribute_value !== null && attribute.attribute_value !== undefined) {
              transformedAttributes[attribute.id] = {
                value: attribute.attribute_value,
                uuid: attribute.id
              };
            }
          });
        }
      });
    }

    return {
      dba_name: apiData.name || '',
      ssn: apiData.ssn || '',
      contact_name: apiData?.contact_name || '',
      contact_phone: apiData.phone || '',
      dba_phone: apiData.phone || '',
      dba_email: apiData.email || '',
      fax_number: apiData.fax_phone || '',
      lead_id: apiData.lead?.id || selectedLeadId || '',
      type: apiData.type || sourceType,
      country_code: apiData.country_code || '',
      dba_address: {
        street: apiData.address || '',
        city: apiData.city || '',
        state: apiData.state?.id || apiData.state_id || '',
        zip_code: apiData.zip || '',
        country: 'US'
      },
      legal_name: apiData.legalIn_formation?.name || '',
      legal_phone: apiData.legalIn_formation?.phone || '',
      legal_email: apiData.legalIn_formation?.email || '',
      legal_fax_number: apiData.legalIn_formation?.fax_phone || '',
      legal_address: {
        street: apiData.legalIn_formation?.address || '',
        city: apiData.legalIn_formation?.city || '',
        state: apiData.state?.id || apiData.state_id || '',
        zip_code: apiData.legalIn_formation?.zip || '',
        country: 'US'
      },
      tax_id: apiData.merchant?.federal_tax_id || '',
      number_of_locations: apiData.merchant?.no_locations || 1,
      years_in_business: apiData.merchant?.business_years || 0,
      months_in_business: apiData.merchant?.business_months || 0,
      ownership_type: apiData.merchant?.ownership || '',
      business_category: mapBusinessCategoryToForm(apiData.merchant?.business_category),
      business_name: apiData?.merchant?.business_name,
      business_description: apiData?.merchant?.business_description,
      avg_month_sales: apiData.merchant?.avg_month_sales || 0,
      business_type: mapBusinessTypeToForm(apiData.merchant?.business_type),
      return_policy: apiData.merchant?.return_policy || '',
      delivery_days: apiData.merchant?.delivery_days || '',
      website: apiData.merchant?.website || '',
      mid_number: apiData.merchant?.mid_number || '',
      banking: {
        bank_name: apiData.bank_account?.bank_name || '',
        routing_number: apiData.bank_account?.routing_number || '',
        account_number: apiData.bank_account?.account_number || '',
        title: apiData?.bank_account?.title || '',
        confirm_account_number: apiData.bank_account?.confirm_account_number || '',
        account_type: mapAccountTypeToForm(apiData.bank_account?.account_type) || 'Checking',
        account_owner: mapAccountOwnerToForm(apiData.bank_account?.account_owner),
        full_name: apiData.bank_account?.full_name || '',
        business_name: apiData.bank_account?.business_name || '',
        mid_number: apiData.merchant?.mid_number || '',
        fee_deduct_other: apiData.bank_account?.fee_deduct_other === 1 || apiData.bank_account?.fee_deduct_other === true,
        present_transaction: apiData.bank_account?.present_transaction || 0,
        moto_transaction: apiData.bank_account?.moto_transaction || 0,
        ecom_transaction: apiData.bank_account?.ecom_transaction || 0
      },
      principals: (apiData.dba_Owners || []).map(owner => {
        const fullName = owner.full_name || '';
        const nameParts = fullName.split(' ');
        return {
          id: owner.id || '',
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          email: owner.email || '',
          mobilePhone: owner.number || '',
          ssn: owner.ssn || '',
          ownershipPercent: owner.ownership_percentage || 0,
          title: owner.designation || '',
          dateOfBirth: owner.doj || '',
          address1: owner.address || '',
          zip: owner.zip || '',
          city: owner.city || '',
          state: owner.state || '',
          driverLicenseNumber: owner.license_no || '',
          driverLicenseExpiration: owner.license_expiry_date || '',
          stateIssued: owner.license_state || '',
          managerController: owner.is_manager === 1 || owner.is_manager === true,
          authorizedSigner: owner.is_authorized === 1 || owner.is_authorized === true,
          bankrupt: mapBankruptcyTypeToForm(owner.bankrupt),
          bankruptDate: owner.bankrupt_date || ''
        };
      }),
      fees: {
        fee_category: apiData.merchant?.fees_category || 'Pass Through',
        use_fee_template: false
      },
      equipment: apiData.equipments || [],
      equipment_notes: apiData.equipment_notes || [],
      documents: apiData.attachments || [],
      attachments: apiData.attachments || [],
      application_addendum: apiData.application_addendum || '',
      additional_attachments: apiData.additional_attachments || '',
      dynamic_attributes: transformedAttributes
    };
  };

  // Helper function to map bankruptcy type from API to form
  const mapBankruptcyTypeToForm = (value) => {
    const mapping = {
      0: 'none',
      1: 'personal',
      2: 'business',
      3: 'both',
      '0': 'none',
      '1': 'personal',
      '2': 'business',
      '3': 'both'
    };
    return mapping[value] || 'none';
  };

  // Helper function to map business category from API to form
  const mapBusinessCategoryToForm = (value) => {
    const mapping = {
      'retail': 'Retail',
      'restaurant': 'Restaurant',
      'services': 'Services',
      'e-commerce': 'E-commerce',
      'other': 'Other'
    };
    return mapping[value?.toLowerCase()] || value || '';
  };

  // Helper function to map business type from API to form
  const mapBusinessTypeToForm = (value) => {
    const mapping = {
      'b2b': 'B2B',
      'b2c': 'B2C',
      'both': 'Both'
    };
    return mapping[value?.toLowerCase()] || value || '';
  };

  // Helper function to map account type from API to form
  const mapAccountTypeToForm = (value) => {
    const mapping = {
      'checking': 'Checking',
      'current': 'Checking',
      'savings': 'Savings'
    };
    return mapping[value?.toLowerCase()] || value || '';
  };

  // Helper function to map account owner from API to form
  const mapAccountOwnerToForm = (value) => {
    const mapping = {
      'individual': 'Personal Account',
      'business': 'Business Account'
    };
    return mapping[value?.toLowerCase()] || value || '';
  };

  const getErrorFieldNames = (errs) => {
    const fieldLabelMap = {
      dba_name: 'DBA Name',
      ssn: 'SSN Number',
      lead_id: 'Lead',
      dba_email: 'DBA Email',
      dba_phone: 'DBA Phone',
      dba_address_street: 'DBA Address',
      dba_address_city: 'DBA City',
      dba_address_state: 'DBA State',
      dba_address_zip_code: 'DBA Zip Code',
      merchant_federal_tax: 'Federal Tax ID/EIN',
      banking_bank_name: 'Bank Name',
      banking_routing_number: 'Routing Number',
      banking_account_number: 'Account Number',
      banking_confirm_account_number: 'Confirm Account Number',
      banking_account_type: 'Account Type',
      banking_payment_methods_sum: 'Payment Methods (must sum to 100%)',
      banking_present_transaction: 'Card Present Transaction %',
      banking_moto_transaction: 'Card MOTO Transaction %',
      banking_ecom_transaction: 'Internet %',
    };
    let keys = Object.keys(errs);
    if (keys.includes('banking_payment_methods_sum')) {
      keys = keys.filter((k) => !['banking_present_transaction', 'banking_moto_transaction', 'banking_ecom_transaction'].includes(k));
    }
    const labels = keys.map((key) => fieldLabelMap[key] || key).filter(Boolean);
    return labels.length > 0 ? labels.join(', ') : 'Required fields';
  };

  const handleNext = async () => {
    const { valid, errors } = validateCurrentStep();
    if (!valid) {
      if (sourceType === 'sales') {
        toast.warning('Some required fields are missing, but you can proceed since this is from a lead.');
      } else {
        const fieldList = getErrorFieldNames(errors);
        toast.error(`Please fix: ${fieldList}`);
      }
      return;
    }

    const stepName = stepPayloadMap[activeStep];
    const stepData = getAllStepData(stepName);

    try {
      let response;
      if (isEditMode) {
        response = await dispatch(updateDBA({ id: editId, dbaData: stepData })).unwrap();
        // console.log(response?.warnings, 'update warninngg')
        if (Array.isArray(response.warnings)) {
          response.warnings.forEach(w => toast.warning(w));
        } else if (response.warnings) {
          toast.warning(response.warnings);
        }
        // toast.warning(response?.warnings);
      } else {
        response = await dispatch(submitAllStepperData(stepData)).unwrap();
        // console.log(response?.warnings, 'c warninngg');
        toast.warning(response?.warnings);

        const newId = response.data?.id || response.id;
        setEditId(newId);
        setIsEditMode(true);
      }

      // ONLY NOW: Sync formData with server response
      const transformedData = transformApiDataToFormData(response);
      setFormData(transformedData);

      // Show appropriate success message
      if (sourceType === 'sales') {
        toast.success('Lead converted to merchant successfully!');
      } else {
        toast.success(response.message || 'hello world');
      }

      if (activeStep === steps.length - 1) {
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        setValidationErrors({});
        setActiveStep(prev => prev + 1);
      }
    } catch (error) {
      const { message, errors } = getValidationErrorDetails(error);
      toast.error(message);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
      }
    }
  };

  // Helper functions to map form values to database enum values
  const mapOwnershipType = (value) => {
    const mapping = {
      'LLC Private': 'llc_private',
      'Partnership': 'partnership',
      'Corporation': 'corporation',
      'Sole Proprietorship': 'sole_proprietorship',
      'LLC Public': 'llc_public'
    };
    return mapping[value] || 'llc_private';
  };

  const mapBusinessCategory = (value) => {
    const mapping = {
      'Retail': 'retail',
      'Financial Services': 'service',
      'Restaurant': 'restaurant',
      'Services': 'service',
      'E-commerce': 'retail',
      'Other': 'other'
    };
    return mapping[value] || 'other';
  };

  const mapBusinessType = (value) => {
    const mapping = {
      'B2B': 'b2b',
      'B2C': 'b2c',
      'Both': 'both'
    };
    return mapping[value] || 'both';
  };

  const mapFeesCategory = (value) => {
    return value || 'Pass Through';
  };

  const mapAccountOwner = (value) => {
    const mapping = {
      'Business Account': 'business',
      'Personal Account': 'individual'
    };
    return mapping[value] || 'individual';
  };

  const mapBankruptcyType = (value) => {
    const mapping = {
      'none': 1,
      'personal': 2,
      'business': 3,
      'both': 4
    };
    return mapping[value] || 0;
  };

  const mapAccountType = (value) => {
    const mapping = {
      'Checking': 'current',
      'Savings': 'savings'
    };
    return mapping[value] || 'current';
  };

  const mapDesignation = (value) => {
    return value?.toLowerCase() || 'president';
  };

  // Validation functions
  // Validation functions
  const validateStep = (step) => {
    const errors = {};

    // Skip all validation if sourceType is 'sales' (coming from lead)
    if (sourceType === 'sales') {
      return errors; // Return empty errors object
    }

    if (step === 0) { // Main step validation
      if (!formData.dba_name || formData.dba_name.trim() === '') {
        errors.dba_name = 'DBA Name is required';
      }

       if (!formData.ssn || formData.ssn.trim() === '') {
        errors.ssn = 'SSN Number is required';
      } else if (!/^\d{9}$/.test(formData.ssn)) {
        errors.ssn = 'SSN Number must be exactly 9 digits';
      }

      if (!formData.lead_id || formData.lead_id.trim() === '') {
        errors.lead_id = 'Lead  is required';
      }

      if (!formData.dba_email || formData.dba_email.trim() === '') {
        errors.dba_email = 'DBA Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.dba_email)) {
        errors.dba_email = 'Please enter a valid email address';
      }

      if (!formData.dba_phone || formData.dba_phone.trim() === '') {
        errors.dba_phone = 'DBA Phone is required';
      }

      if (!formData.dba_address?.street || formData.dba_address.street.trim() === '') {
        errors.dba_address_street = 'DBA Address is required';
      }

      if (!formData.dba_address?.city || formData.dba_address.city.trim() === '') {
        errors.dba_address_city = 'DBA City is required';
      }

      if (!formData.dba_address?.state || formData.dba_address.state === '') {
        errors.dba_address_state = 'DBA State is required';
      }

      if (!formData.dba_address?.zip_code || formData.dba_address.zip_code.trim() === '') {
        errors.dba_address_zip_code = 'DBA Zip Code is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.dba_address.zip_code)) {
        errors.dba_address_zip_code = 'Please enter a valid zip code (12345 or 12345-6789)';
      }
    }

    if (step === 1) { // Profile step validation - Banking Information


      if (!formData.tax_id || formData.tax_id.trim() === '') {
        errors.merchant_federal_tax = 'Federal Tax ID/EIN is required';
      }

      if (!formData.banking?.bank_name || formData.banking.bank_name.trim() === '') {
        errors.banking_bank_name = 'Bank Name is required';
      }




    

      if (!formData.banking?.routing_number || formData.banking.routing_number.trim() === '') {
        errors.banking_routing_number = 'Routing Number is required';
      } else if (!/^\d{9}$/.test(formData.banking.routing_number)) {
        errors.banking_routing_number = 'Routing Number must be exactly 9 digits';
      }

      if (!formData.banking?.account_number || formData.banking.account_number.trim() === '') {
        errors.banking_account_number = 'Account Number is required';
      } else if (formData.banking.account_number.length < 4) {
        errors.banking_account_number = 'Account Number must be at least 4 digits';
      }

      const presentTransaction = parseFloat(formData.banking?.present_transaction) || 0;
      const motoTransaction = parseFloat(formData.banking?.moto_transaction) || 0;
      const ecomTransaction = parseFloat(formData.banking?.ecom_transaction) || 0;

      if (presentTransaction < 0 || presentTransaction > 100) {
        errors.banking_present_transaction = 'Present Transaction % must be between 0 and 100';
      }

      if (motoTransaction < 0 || motoTransaction > 100) {
        errors.banking_moto_transaction = 'MOTO Transaction % must be between 0 and 100';
      }

      if (ecomTransaction < 0 || ecomTransaction > 100) {
        errors.banking_ecom_transaction = 'E-commerce Transaction % must be between 0 and 100';
      }

      // Payment Methods: sum of the three percentages must equal 100%
      const paymentSum = presentTransaction + motoTransaction + ecomTransaction;
      const sumsTo100 = Math.abs(paymentSum - 100) < 0.01;
      if (!sumsTo100) {
        errors.banking_payment_methods_sum = 'The sum of Card Present, Card MOTO, and Internet percentages must equal 100%';
        errors.banking_present_transaction = errors.banking_present_transaction || 'Must sum to 100%';
        errors.banking_moto_transaction = errors.banking_moto_transaction || 'Must sum to 100%';
        errors.banking_ecom_transaction = errors.banking_ecom_transaction || 'Must sum to 100%';
      }

      if (formData.banking?.confirm_account_number &&
        formData.banking.account_number !== formData.banking.confirm_account_number) {
        errors.banking_confirm_account_number = 'Account numbers do not match';
      }
    }

    return errors;
  };

  const validateCurrentStep = () => {
    // Skip validation if sourceType is "sales" (coming from lead)
    if (sourceType === 'sales') {
      setValidationErrors({});
      return { valid: true, errors: {} };
    }

    const errors = validateStep(activeStep);
    setValidationErrors(errors);
    return { valid: Object.keys(errors).length === 0, errors };
  };

  const getAllStepData = (stepName) => {
    const safeFormData = formData || {};

    const payload = { step: stepName,lead_id: leadIdFromUrl || safeFormData.lead_id || '' };

    if (stepName === 'dba') {
      payload.dba = {
        name: safeFormData.dba_name || '',
        ssn: safeFormData.ssn || '', 
        contact_name: safeFormData.contact_name || '',
        email: safeFormData.dba_email || '',
        phone: safeFormData.dba_phone || '',
        fax_phone: safeFormData.fax_number || '',
        address: safeFormData.dba_address?.street || '',
        city: safeFormData.dba_address?.city || '',
        state_id: safeFormData.dba_address?.state || '',
        zip: safeFormData.dba_address?.zip_code || '',
        status: safeFormData?.status || 1,
        lead_id: leadIdFromUrl || safeFormData.lead_id || '',   // ← ALWAYS include this
        type: safeFormData?.type || 'normal', // Include type

      };
      payload.legal_information = {
        name: safeFormData.legal_name || '',
        email: safeFormData.legal_email || '',
        phone: safeFormData.legal_phone || '',
        fax_phone: safeFormData.legal_fax_number || '',
        address: safeFormData.legal_address?.street || '',
        city: safeFormData.legal_address?.city || '',
        state_id: safeFormData.legal_address?.state || '',
        zip: safeFormData.legal_address?.zip_code || ''
      };
    } else if (stepName === 'merchant') {
      payload.merchant = {
        federal_tax_id: safeFormData.tax_id || '',
        mid_number: safeFormData.mid_number || '',
        no_locations: safeFormData.number_of_locations || 1,
        business_years: safeFormData.years_in_business || 0,
        business_name: safeFormData.business_name || '',
        mid_number: safeFormData.mid_number || '',
        business_description: safeFormData.business_description || '',
        business_months: safeFormData.months_in_business || 0,

        ownership_type: mapOwnershipType(safeFormData.ownership_type || ''),
        business_category: mapBusinessCategory(safeFormData.business_category || ''),
        avg_month_sales: safeFormData.avg_month_sales || 0,
        business_type: mapBusinessType(safeFormData.business_type || ''),
        return_policy: safeFormData.return_policy || '',
        delivery_days: safeFormData.delivery_days || '',
        website: safeFormData.website || '',
        fees_category: mapFeesCategory(safeFormData.fes?.fee_category || 'Pass Through')
      };
      payload.bank_account = {
        bank_name: safeFormData.banking?.bank_name || '',
        full_name: safeFormData.banking?.full_name || '',
        account_owner: mapAccountOwner(safeFormData.banking?.account_owner || ''),
        bussiness_name: safeFormData.banking?.bussiness_name || '',
        title: safeFormData?.banking?.title || '',
        account_type: mapAccountType(safeFormData.banking?.account_type || 'checking'),
        routing_number: safeFormData.banking?.routing_number || '',
        account_number: safeFormData.banking?.account_number || '',
        fee_deduct_other: safeFormData.banking?.fee_deduct_other || false,
        present_transaction: safeFormData.banking?.present_transaction || 0,
        moto_transaction: safeFormData.banking?.moto_transaction || 0,
        ecom_transaction: safeFormData.banking?.ecom_transaction || 0
      };
      payload.attributes = safeFormData.dynamic_attributes ? Object.entries(safeFormData.dynamic_attributes)
        .filter(([attributeId, valueOrObject]) => {
          const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
          return attributeId &&
            attributeId !== 'null' &&
            attributeId !== 'undefined' &&
            value &&
            value !== '0.0000' &&
            value !== '0' &&
            value !== '';
        })
        .map(([attributeId, valueOrObject]) => {
          const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
          const uuid = typeof valueOrObject === 'object' ? valueOrObject.uuid : null;
          return {
            attribute_id: isEditMode && uuid ? uuid : attributeId,
            value: value
          };
        }) : [];
    } else if (stepName === 'principals') {
      payload.principals = (safeFormData.principals || []).map(principal => ({
        id: principal?.id || '',
        full_name: `${principal.firstName || ''} ${principal.lastName || ''}`.trim(),
        email: principal.email || '',
        number: principal.phone || principal.mobilePhone || '',
        ssn: principal.ssn || '',
        ownership_percentage: principal.ownershipPercent || 0,
        designation: mapDesignation(principal.title || ''),
        doj: principal.dateOfBirth || null,
        address: principal.address || principal.address1 || '',
        city: principal.city || '',
        state: principal.state || '',
        zip: principal.zip || '',
        license_no: principal.driverLicenseNumber || '',
        license_expiry_date: principal.driverLicenseExpiration || null,
        license_state: principal.stateIssued || '',
        is_manager: principal.managerController || false,
        is_authorized: principal.authorizedSigner || false,
        bankrupt: mapBankruptcyType(principal.bankrupt),
        bankrupt_date: principal.bankruptDate || null,
        owners_terminated_bankcards: false
      }));
    } else if (stepName === 'equipment') {
      payload.equipment = safeFormData.equipment || [];
      payload.equipment_notes = safeFormData.equipment_notes || [];
      payload.attributes = safeFormData.dynamic_attributes ? Object.entries(safeFormData.dynamic_attributes)
        .filter(([attributeId, valueOrObject]) => {
          const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
          return attributeId &&
            attributeId !== 'null' &&
            attributeId !== 'undefined' &&
            value &&
            value !== '0.0000' &&
            value !== '0' &&
            value !== '';
        })
        .map(([attributeId, valueOrObject]) => {
          const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
          const uuid = typeof valueOrObject === 'object' ? valueOrObject.uuid : null;
          return {
            attribute_id: isEditMode && uuid ? uuid : attributeId,
            value: value
          };
        }) : [];
    } else if (stepName === 'attachments') {
      payload.attachments = safeFormData.attachments || [];
      payload.application_addendum = safeFormData.application_addendum || '';
      payload.additional_attachments = safeFormData.additional_attachments || '';
    }

    return payload;
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    // setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const updateFormData = (stepData) => {
    setFormData(prevData => {
      const newData = { ...prevData };
      Object.keys(stepData).forEach(key => {
        if (typeof stepData[key] === 'object' && stepData[key] !== null && !Array.isArray(stepData[key])) {
          newData[key] = { ...prevData[key], ...stepData[key] };
        } else {
          newData[key] = stepData[key];
        }
      });
      return newData;
    });
  };

  const getStepContent = (step) => {
    const stepProps = {
      merchantData,
      isEditMode,
      missingItems,
      formData,
      updateFormData,
      validationErrors,
      sourceType, // Pass sourceType
      selectedLeadId // Pass selectedLeadId
    };

    switch (step) {
      case 0:
        return <MainStep {...stepProps} />;
      case 1:
        return <ProfileStep {...stepProps} />;
      case 2:
        return <PrincipalsStep {...stepProps} />;
      case 3:
        return <FeesStep {...stepProps} />;
      case 4:
        return <EquipmentStep {...stepProps} />;
      case 5:
        return <DocsStep {...stepProps} />;
      default:
        return 'Unknown step';
    }
  };

  useEffect(() => {
    dispatch(getAttributeTypes({
      relations: ['attributes'],
      per_page: 100,
    })).catch(error => {
      console.error('Error fetching attributes:', error);
    });
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 5 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h4"
                sx={{ color: 'blue.600', fontWeight: 600 }}
              >
                {isEditMode ? 'Edit Merchant' : 'Create Merchant'}
              </Typography>
              {isEditMode && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Completing missing requirements: {missingItems.join(', ')}
                </Typography>
              )}
            </Box>
            <StepperForm steps={steps} activeStep={activeStep} />
            {isEditMode && isLoadingData ? (
              <Box sx={{
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: 1,
                border: 1,
                borderColor: 'grey.200',
                p: 4,
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Loading merchant data...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we fetch the merchant information.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, border: 1, borderColor: 'grey.200' }}>
                {getStepContent(activeStep)}
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <ActionButton
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ px: 4, py: 1 }}
              >
                Back
              </ActionButton>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ActionButton
                  disabled={false}
                  onClick={handleNext}
                  sx={{
                    px: 4,
                    py: 1,
                    bgcolor: activeStep === steps.length - 1 ? '#4caf50' : '#1976d2',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: activeStep === steps.length - 1 ? '#45a049' : '#1565c0'
                    },
                    '&:disabled': {
                      bgcolor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  {activeStep === steps.length - 1 ? '✓ Finish' : 'Next'}
                </ActionButton>
              </Box>
            </Box>
        </Container>
      </PageLayout>
    </ErrorBoundary>
  );
};

export default CreateMerchant;